/**
 * ヒーローセクションのタイプライターアニメーションを制御する
 *
 * 処理の流れ:
 * 1. 表示対象の要素と表示するテキスト行を用意する
 * 2. 1文字ずつ追加しながら描画し、タイプしていく演出を作る
 * 3. 全文字を表示し終えたら末尾に「.」を表示して終了する
 */
export function initHeroTypewriter() {
	'use strict';

	// テキストを挿入するターゲット要素を取得する
	const target = document.getElementById('engineer-hero-typewriter');

	// 要素が存在しない場合は処理を中断する
	if (!target) {
		return;
	}

	// 画面幅に応じて改行位置を変える
	// モバイル/タブレットは3行、デスクトップは2行
	const isMobile = window.matchMedia('(max-width: 1024px)').matches;
	const lines = isMobile
		? ['GROW', 'THE VISION', 'WITH US']
		: ['GROW THE VISION', 'WITH US'];

	// 全行を結合したときの合計文字数（タイプ完了の判定に使う）
	const totalLength = lines.join('').length;

	// -------------------------------------------------------
	// タイミング設定（ミリ秒）
	// -------------------------------------------------------
	const TIMING = {
		startDelay:   600,  // ページ読み込み後、アニメーション開始までの待機時間
		typeInterval: 115,  // 1文字タイプするごとの間隔
		dotDelay:     400,  // 全文字入力完了後、「.」が出るまでの待機時間
	};

	// -------------------------------------------------------
	// アニメーションの状態管理フラグ
	// -------------------------------------------------------
	let currentIndex = 0;   // 現在表示している文字数
	let showDot = false;    // true のとき末尾に「.」を表示する

	// -------------------------------------------------------
	// 指定した文字数分のテキストをHTMLとして描画する関数
	//
	// length: 先頭から何文字分を表示するか
	// 複数行にまたがる場合は各行ごとに切り分けて <span> で囲む
	// -------------------------------------------------------
	const render = (length) => {
		// 各行が全体テキストの何文字目から始まるか追跡する
		let charIndex = 0;

		const html = lines.map((line, index) => {
			// この行の開始位置と終了位置（全体テキスト上）
			const lineStart = charIndex;
			const lineEnd = charIndex + line.length;

			// display 対象範囲 [0, length) とこの行がどれだけ重なるか
			const visibleEnd = Math.min(length, lineEnd);
			const visibleText = visibleEnd > lineStart
				? line.slice(0, visibleEnd - lineStart)
				: '';

			// 次の行のために charIndex を進める
			charIndex += line.length;

			// 最終行かつ showDot が true なら末尾に「.」を追加する
			const dot = showDot && index === lines.length - 1
				? '<span class="hero__titleDot">.</span>'
				: '';

			return '<span class="hero__titleLine">' + visibleText + dot + '</span>';
		}).join('');

		target.innerHTML = html;
	};

	// -------------------------------------------------------
	// アニメーションを1ステップ進める関数
	//
	// 1文字ずつ追加して描画し、全文字入力完了後は
	// dotDelay 後に「.」を表示してアニメーションを終了する
	// -------------------------------------------------------
	const tick = () => {
		currentIndex += 1;
		render(currentIndex);

		if (currentIndex === totalLength) {
			// 全文字入力完了 → dotDelay後に「.」を表示して終了
			window.setTimeout(() => {
				showDot = true;
				render(currentIndex);
			}, TIMING.dotDelay);
			return;
		}

		window.setTimeout(tick, TIMING.typeInterval); // 次の1文字入力まで待つ
	};

	// 初期状態（0文字）で描画してから、startDelay後にアニメーション開始
	render(0);
	window.setTimeout(tick, TIMING.startDelay);
}

